import { useNode } from "@craftjs/core";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { ButtonLinkBlockSettings } from "@/components/craft/settings/ButtonLinkBlockSettings";

type ButtonLinkBlockProps = {
  label: string;
  href: string;
  variant?: "primary" | "secondary" | "ghost";
  fullWidth?: boolean;
  style?: ResponsiveStyleProps;
};

export function ButtonLinkBlock({
  label,
  href = "/contact",
  variant = "primary",
  fullWidth = false,
  style = defaultStyleProps(),
}: ButtonLinkBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));

  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);

  const base =
    "inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-base font-semibold transition";
  const styles = {
    primary: "border border-gold bg-transparent text-gold hover:bg-gold/10",
    secondary:
      "border border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground",
    ghost: "text-primary hover:bg-primary/8 rounded-md",
  } as const;

  const className = cn(
    base,
    styles[variant],
    fullWidth && "w-full",
    styleClass,
    selected && "outline outline-2 outline-dashed outline-primary/40",
  );

  const isExternal = href.startsWith("http") || href.startsWith("mailto:");

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn("my-4", fullWidth && "w-full", `craft-node-${id}`)}
      style={inlineStyle}
    >
      {isExternal ? (
        <a href={href} className={className}>
          {label}
          <ArrowRight className="h-4 w-4" />
        </a>
      ) : (
        <Link to={href as never} className={className}>
          {label}
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

ButtonLinkBlock.craft = {
  displayName: "Button link",
  props: {
    label: "Learn more",
    href: "/contact",
    variant: "primary",
    fullWidth: false,
    style: defaultStyleProps(),
  },
  rules: {
    canDrag: () => true,
  },
  related: {
    settings: ButtonLinkBlockSettings,
  },
};
